import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastModule } from 'primeng/toast';
import { MenuItem, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { Menu, MenuModule } from 'primeng/menu';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';

import {
  SearchFormComponent,
  SearchFormData,
  FormFieldConfig,
} from '@shared/components/search-form/search-form.component';
import {
  PaginationEvent,
  TableColumn,
} from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import {
  ActionButton,
  StatusFilterComponent,
} from '@app/shared/components/status-filter/status-filter.component';
import {
  ConfirmationButton,
  ConfirmationDialogComponent,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';

import {
  ACCOUNT_COLUMNS,
  ACCOUNT_FLEXIBLE_COLUMNS,
  ACCOUNT_SEARCH_CONFIG,
  TEMPLATE_HEALTH,
  TemplateHealth,
  getRetryToast,
  getStatusDisplayText,
  getStatusSeverity,
} from './e-invoice-account.config';
import {
  EInvoiceAccount,
  EInvoiceAccountPageRequest,
  EInvoiceAccountService,
} from './services/e-invoice-account.service';
import { EInvoiceProviderService } from '../provider/services/e-invoice-provider.service';
import { PermissionService } from '@app/core/services/permission.service';
import { withPaginatedSearchFields } from '@shared/utils/paginated-field.helpers';

@Component({
  selector: 'app-e-invoice-account',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ToastModule,
    TagModule,
    ButtonModule,
    MenuModule,
    TooltipModule,
    SearchFormComponent,
    AdvancedDataTableComponent,
    StatusFilterComponent,
    ConfirmationDialogComponent,
  ],
  providers: [MessageService],
  templateUrl: './e-invoice-account.component.html',
  styleUrls: [
    '../shared/e-invoice-table.css',
    './e-invoice-account.component.css',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EInvoiceAccountComponent implements OnInit, OnDestroy {
  // Pagination
  first = 0;
  rows = 20;

  // Configuration
  searchFormConfig = signal<FormFieldConfig[]>([...ACCOUNT_SEARCH_CONFIG]);
  readonly defaultColumns: TableColumn[] = ACCOUNT_COLUMNS;
  readonly flexibleColumns: TableColumn[] = ACCOUNT_FLEXIBLE_COLUMNS;

  // Data signals
  dataSource = signal<EInvoiceAccount[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  // Search parameters
  searchParams = signal<EInvoiceAccountPageRequest>({
    providerId: undefined,
    status: undefined,
    quotaExhausted: undefined,
    page: 0,
    size: 20,
  });

  // Pre-fills the search form when filters are applied from outside it
  presetValues = signal<SearchFormData | null>(null);

  hasActiveFilters = computed(() => {
    const { providerId, status, quotaExhausted } = this.searchParams();
    return (
      providerId !== undefined ||
      status !== undefined ||
      quotaExhausted !== undefined
    );
  });
  isEmpty = computed(() => !this.loading() && this.dataSource().length === 0);

  // Button configuration
  readonly primaryButton: ActionButton = {
    label: 'common.actions.new',
    severity: 'info',
    show: true,
    icon: 'add',
  };

  readonly secondaryButton: ActionButton = {
    label: '',
    severity: 'success',
    outlined: true,
    show: true,
    icon: 'view_column',
  };

  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;
  @ViewChild('actionMenu') actionMenu?: Menu;
  currentMenuModel = signal<MenuItem[]>([]);
  openMenuAccountId = signal<number | null>(null);
  // Rows with a retry in flight; keeps the rest of the table interactive
  retryingAccountIds = signal<ReadonlySet<number>>(new Set());
  private menuTrigger: HTMLElement | null = null;

  // Confirmation dialog state
  showConfirmationDialog = signal(false);
  confirmationTitle = signal('');
  confirmationMessage = signal('');
  confirmationButtons = signal<ConfirmationButton[]>([]);
  selectedAccountForAction = signal<EInvoiceAccount | null>(null);
  currentAction = signal<'delete' | 'status' | 'retry' | null>(null);

  private readonly destroy$ = new Subject<void>();
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly accountService = inject(EInvoiceAccountService);
  private readonly providerService = inject(EInvoiceProviderService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );

  canView = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.e_invoice_account.view');
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.e_invoice_account.create');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.e_invoice_account.edit');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.e_invoice_account.delete');
  });

  ngOnInit(): void {
    this.searchFormConfig.set(
      withPaginatedSearchFields([...ACCOUNT_SEARCH_CONFIG], {
        providerId: {
          loadPage: (params) => this.providerService.getProviderOptionsPage(params),
        },
      }),
    );
    this.loadAccounts();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadAccounts(): void {
    this.loading.set(true);

    this.accountService
      .searchAccounts(this.searchParams())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.dataSource.set(response.pageContent || []);
          this.totalRecords.set(response.totalElements || 0);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'e_invoice.account.messages.error.failed_to_load',
            ),
          });
        },
      });
  }

  onSearch(searchData: SearchFormData): void {
    this.first = 0;
    this.searchParams.set({
      ...this.searchParams(),
      providerId:
        searchData?.['providerId'] != null
          ? (searchData['providerId'] as number)
          : undefined,
      status:
        searchData?.['status'] != null
          ? (searchData['status'] as boolean)
          : undefined,
      quotaExhausted: this.toQuotaExhausted(
        searchData?.['health'] as TemplateHealth | null | undefined,
      ),
      page: 0,
    });
    this.loadAccounts();
  }

  private toQuotaExhausted(
    health: TemplateHealth | null | undefined,
  ): boolean | undefined {
    return health ? health === TEMPLATE_HEALTH.EXHAUSTED : undefined;
  }

  onReset(): void {
    this.first = 0;
    this.searchParams.set({
      providerId: undefined,
      status: undefined,
      quotaExhausted: undefined,
      page: 0,
      size: this.rows,
    });
    this.loadAccounts();
  }

  /** Clears every filter, including the values shown in the search form. */
  onClearFilters(): void {
    this.presetValues.set({ providerId: null, status: null, health: null });
    this.onReset();
  }

  onPageChange(event: PaginationEvent): void {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? this.rows;
    this.searchParams.set({
      ...this.searchParams(),
      page: this.first / this.rows,
      size: this.rows,
    });
    this.loadAccounts();
  }

  // Display helpers
  getStatusSeverity(
    status: boolean,
  ): 'success' | 'warn' | 'danger' | 'secondary' {
    return getStatusSeverity(status);
  }

  getStatusDisplayText(status: boolean): string {
    return getStatusDisplayText(status, this.translateService);
  }

  /** Highlights accounts that cannot issue invoices or have failed invoices pending. */
  getRowClass = (row: EInvoiceAccount): string => {
    if (row.quotaExhausted) {
      return 'row-problem row-problem-danger';
    }
    return row.pendingFailedCount > 0
      ? 'row-problem row-problem-warn'
      : '';
  };

  onAdd(): void {
    this.router.navigate(['/configuration/e-invoice/account/add']);
  }

  onRetryPending(account: EInvoiceAccount): void {
    this.selectedAccountForAction.set(account);
    this.currentAction.set('retry');
    this.confirmationTitle.set(
      this.translateService.instant('common.confirmation.title'),
    );
    const count = account.pendingFailedCount;
    const batch = account.retryBatchSize ?? 0;
    this.confirmationMessage.set(
      this.translateService.instant(
        batch > 0
          ? 'e_invoice.account.messages.confirmation.retry_pending_batch'
          : 'e_invoice.account.messages.confirmation.retry_pending',
        { count, batch },
      ),
    );
    this.confirmationButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant(
          'e_invoice.account.actions.retry_pending_short',
        ),
        action: 'confirm',
        severity: 'info',
      },
    ]);
    this.showConfirmationDialog.set(true);
  }

  private handleRetryPending(account: EInvoiceAccount): void {
    this.setRetrying(account.id, true);
    this.accountService
      .retryPendingOrders(account.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          const toast = getRetryToast(result);
          this.messageService.add({
            severity: toast.severity,
            summary: this.translateService.instant(toast.summaryKey),
            detail: this.translateService.instant(toast.detailKey, {
              resolved: result.resolved ?? 0,
              remaining: result.remainingPending ?? 0,
            }),
          });
          this.refreshRow(account, result.remainingPending);
        },
        error: (error) => {
          this.setRetrying(account.id, false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              this.accountService.getErrorTranslationKey(error) ??
                'e_invoice.account.messages.error.failed_to_retry',
              this.accountService.getErrorParams(error),
            ),
          });
        },
      });
  }

  /** Reloads only the retried row; falls back to the replay's remaining count. */
  private refreshRow(account: EInvoiceAccount, remainingPending = 0): void {
    this.accountService
      .getAccountById(account.id, account.providerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (fresh) => this.replaceRow(fresh),
        error: () =>
          this.replaceRow({ ...account, pendingFailedCount: remainingPending }),
      });
  }

  private replaceRow(updated: EInvoiceAccount): void {
    this.dataSource.update((rows) =>
      rows.map((row) => (row.id === updated.id ? updated : row)),
    );
    this.setRetrying(updated.id, false);
  }

  private setRetrying(accountId: number, retrying: boolean): void {
    this.retryingAccountIds.update((ids) => {
      const next = new Set(ids);
      if (retrying) {
        next.add(accountId);
      } else {
        next.delete(accountId);
      }
      return next;
    });
  }

  hasOthersActions(): boolean {
    return this.canUpdate() || this.canDelete();
  }

  toggleOthersMenu(event: Event, account: EInvoiceAccount): void {
    event.preventDefault();
    event.stopPropagation();
    const items: MenuItem[] = [];
    if (this.canUpdate()) {
      items.push({
        label: this.translateService.instant(
          account.status
            ? 'common.table.actions.deactivate'
            : 'common.table.actions.activate',
        ),
        command: () => this.onStatusChange(account, !account.status),
      });
    }
    if (this.canDelete()) {
      items.push({
        label: this.translateService.instant('common.actions.delete'),
        styleClass: 'text-red-600',
        command: () => this.onDelete(account),
      });
    }
    this.currentMenuModel.set(items);
    this.openMenuAccountId.set(account.id);
    this.menuTrigger = event.currentTarget as HTMLElement | null;
    this.actionMenu?.toggle(event);
  }

  /** Closes the Others menu and returns focus to its button unless a dialog took over. */
  onMenuHide(): void {
    this.openMenuAccountId.set(null);
    if (!this.showConfirmationDialog()) {
      this.menuTrigger?.focus();
    }
    this.menuTrigger = null;
  }

  onView(account: EInvoiceAccount): void {
    this.router.navigate(
      ['/configuration/e-invoice/account/view', account.id],
      {
        queryParams: { providerId: account.providerId },
      },
    );
  }

  onEdit(account: EInvoiceAccount): void {
    this.router.navigate(
      ['/configuration/e-invoice/account/edit', account.id],
      {
        queryParams: { providerId: account.providerId },
      },
    );
  }

  onDelete(account: EInvoiceAccount): void {
    this.selectedAccountForAction.set(account);
    this.currentAction.set('delete');
    this.confirmationTitle.set(
      this.translateService.instant('common.confirmation.title'),
    );
    this.confirmationMessage.set(
      this.translateService.instant(
        'e_invoice.account.messages.confirmation.delete',
        { name: account.username || '' },
      ),
    );
    this.confirmationButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('common.actions.delete'),
        action: 'confirm',
        severity: 'danger',
      },
    ]);
    this.showConfirmationDialog.set(true);
  }

  onStatusChange(account: EInvoiceAccount, newStatus: boolean): void {
    const isActivating = newStatus;
    this.selectedAccountForAction.set(account);
    this.currentAction.set('status');
    this.confirmationTitle.set(
      this.translateService.instant('common.confirmation.title'),
    );
    this.confirmationMessage.set(
      this.translateService.instant(
        isActivating
          ? 'e_invoice.account.messages.confirmation.activate'
          : 'e_invoice.account.messages.confirmation.deactivate',
        { name: account.username || '' },
      ),
    );
    this.confirmationButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant(
          isActivating
            ? 'common.table.actions.activate'
            : 'common.table.actions.deactivate',
        ),
        action: 'confirm',
        severity: 'info',
      },
    ]);
    this.showConfirmationDialog.set(true);
  }

  onConfirmationDialogAction(action: string): void {
    if (action === 'cancel') {
      this.closeConfirmationDialog();
      return;
    }

    if (action !== 'confirm') {
      this.closeConfirmationDialog();
      return;
    }

    const account = this.selectedAccountForAction();
    const actionType = this.currentAction();

    if (!account || !actionType) {
      this.closeConfirmationDialog();
      return;
    }

    if (actionType === 'delete') {
      this.handleDeleteAccount(account);
      return;
    }

    if (actionType === 'retry') {
      this.closeConfirmationDialog();
      this.handleRetryPending(account);
      return;
    }

    if (actionType === 'status') {
      this.handleStatusChange(account, !account.status);
      return;
    }

    this.closeConfirmationDialog();
  }

  private handleDeleteAccount(account: EInvoiceAccount): void {
    this.loading.set(true);
    this.accountService
      .deleteAccount(account)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'e_invoice.account.messages.success.deleted',
            ),
          });

          if (this.dataSource().length === 1 && this.first > 0) {
            this.first = Math.max(0, this.first - this.rows);
            this.searchParams.set({
              ...this.searchParams(),
              page: this.first / this.rows,
            });
          }

          this.closeConfirmationDialog();
          this.loadAccounts();
        },
        error: (error) => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              this.accountService.getErrorTranslationKey(error) ??
                'e_invoice.account.messages.error.failed_to_delete',
              this.accountService.getErrorParams(error),
            ),
          });
        },
      });
  }

  private handleStatusChange(
    account: EInvoiceAccount,
    newStatus: boolean,
  ): void {
    this.loading.set(true);

    this.accountService
      .updateAccount(account.id, {
        providerId: account.providerId,
        status: newStatus,
        companyId: account.companyId,
        companyName: account.companyName,
        userName: account.username,
        invoiceTemplate: account.invoiceTemplate,
        invoiceSeries: account.invoiceSeries,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              newStatus
                ? 'e_invoice.account.messages.success.activated'
                : 'e_invoice.account.messages.success.deactivated',
            ),
          });
          this.closeConfirmationDialog();
          this.loadAccounts();
        },
        error: (error) => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              this.accountService.getErrorTranslationKey(error) ??
                'e_invoice.account.messages.error.failed_to_update_status',
              this.accountService.getErrorParams(error),
            ),
          });
        },
      });
  }

  private closeConfirmationDialog(): void {
    this.showConfirmationDialog.set(false);
    this.selectedAccountForAction.set(null);
    this.currentAction.set(null);
  }

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }
}
